import type { ReactNode } from "react";

import { classes } from "./classes.js";
import { BANK_FIGURE } from "./tokens.js";

/**
 * `F-0c`: a ring that says how far (prototype `.ring`): the offer's validity, the conditions' progress.
 *
 * Drawn as SVG (a circle's `stroke-dashoffset`), not as a CSS conic gradient, so it prints, scales and needs no
 * colour literal. It is a named `progressbar`; the centre is the caller's (a figure and its unit).
 */
export interface ProgressRingProps {
  /** The ring's accessible name, from the catalogue. */
  readonly label: string;
  /** 0..100, clamped. */
  readonly value: number;
  readonly size?: number;
  readonly children?: ReactNode;
  readonly className?: string;
}

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function ProgressRing({
  label,
  value,
  size = 120,
  children,
  className,
}: ProgressRingProps): React.JSX.Element {
  const clamped = Math.min(100, Math.max(0, value));
  const offset = CIRCUMFERENCE * (1 - clamped / 100);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      data-kit="ring"
      className={classes("relative inline-grid shrink-0 place-items-center", className)}
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true" className="-rotate-90">
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="9"
          className="stroke-[color:var(--hk-line)]"
        />
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          className="stroke-[color:var(--hk-ok)]"
        />
      </svg>
      {children === undefined ? null : (
        <div
          className={classes(
            "absolute inset-0 grid place-items-center text-center leading-tight",
            BANK_FIGURE,
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}
